import {
  Button,
  DatePicker,
  Table,
  Tag,
  Row,
  Col,
  message,
  Select,
  Card,
  Popover,
} from "antd";
import React, { useEffect, useState } from "react";
import { DownloadOutlined, SearchOutlined } from "@ant-design/icons";
import moment from "moment";
import dayjs from "dayjs";
import { get_disease_report, get_unique_test_names } from "../../../endpoints/API";

const { RangePicker } = DatePicker;
const { Option } = Select;

const DiseaseReport = () => {
  const [data, setData] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedTests, setSelectedTests] = useState([]);
  const [testOptions, setTestOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);

  useEffect(() => {
    const fetchTestNames = async () => {
      try {
        setDropdownLoading(true);
        const response = await get_unique_test_names();
        if (response.status === 200) setTestOptions(response.data.tests || []);
      } catch (error) {
        message.error("Failed to load test names");
      } finally {
        setDropdownLoading(false);
      }
    };
    fetchTestNames();
  }, []);

  const fetchReportData = async (dates, tests) => {
    try {
      setLoading(true);
      const response = await get_disease_report({ tests, startDate: dates?.[0], endDate: dates?.[1] });
      if (response.status !== 200) throw new Error("Failed to fetch data");
      setData(response.data.data || []);
    } catch (error) {
      message.error(error?.response?.data?.error || "Error fetching report");
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (value, dateString) => {
    setSelectedDates(dateString);
  };

  const handleTestChange = (values) => {
    setSelectedTests(values);
  };

  const handleSearch = () => {
    if (!selectedDates.length) {
      message.warning("Please select date range");
      return;
    }
    fetchReportData(selectedDates, selectedTests);
  };

  const handleDownload = () => {
    if (!data.length) return;
    const headers = ["Patient Name", "Age", "Phone", "Gender", "Health Conditions", "Test Name", "Total Test Count", "Test Date"];
    let rows = [];
    data.forEach((row) => {
      row.test_date?.forEach((date) => {
        rows.push([
          row.patient_name,
          row.age,
          row.phone,
          row.gender,
          row.health_conditions?.join(", "),
          row.test_name,
          row.test_count_for_patient,
          moment(date).format("DD/MM/YYYY hh:mm A"),
        ]);
      });
    });
    const csvContent = [headers.join(",")].concat(rows.map((r) => r.join(","))).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "DiseaseReport.csv";
    link.click();
  };

  const columns = [
    { title: "Patient Name", dataIndex: "patient_name", key: "patient_name", width: 130, fixed: "left" },
    { title: "Age", dataIndex: "age", key: "age", width: 70 },
    { title: "Phone", dataIndex: "phone", key: "phone", width: 120 },
    { title: "Gender", dataIndex: "gender", key: "gender", width: 90 },
    {
      title: "Health Conditions", dataIndex: "health_conditions", key: "health_conditions", width: 180,
      render: (conditions) =>
        conditions?.map((condition, index) => (
          <Tag color="geekblue" key={index}>{condition}</Tag>
        )),
    },
    { title: "Test Name", dataIndex: "test_name", key: "test_name", width: 150 },
    {
      title: "Total Test Count", dataIndex: "test_count_for_patient", key: "test_count_for_patient", width: 120,
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "Test Dates", dataIndex: "test_date", key: "test_date", width: 160,
      render: (dates) => {
        if (!dates || dates.length === 0) return "-";
        const formattedDates = dates.map((date) => moment(date).format("DD/MM/YYYY hh:mm A"));
        if (formattedDates.length <= 2) {
          return formattedDates.map((d, i) => <div key={i}>{d}</div>);
        }
        return (
          <Popover
            content={
              <div style={{ maxHeight: 200, overflowY: "auto" }}>
                {formattedDates.map((d, i) => <div key={i}>{d}</div>)}
              </div>
            }
            title="All Test Dates"
            trigger="click"
          >
            <div style={{ cursor: "pointer", color: "#fffff" }}>
              {formattedDates[0]}<br />
              {formattedDates[1]}<br />
              +{formattedDates.length - 2} more
            </div>
          </Popover>
        );
      },
    },
  ];

  return (
    <div style={{ padding: "12px" }}>
      <Card bordered style={{ borderRadius: 10 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={4}>
            <div style={{ fontWeight: 600 }}>
              Total Records: <Tag color="purple">{data?.length}</Tag>
            </div>
          </Col>

          <Col xs={24} md={8}>
            <Select
              mode="multiple"
              allowClear
              placeholder="Select Test(s)"
              style={{ width: "100%" }}
              onChange={handleTestChange}
              loading={dropdownLoading}
              showSearch
              optionFilterProp="children"
            >
              {testOptions.map((test) => (
                <Option key={test} value={test}>{test}</Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} md={6}>
            <RangePicker
              format="YYYY-MM-DD"
              onChange={handleDateChange}
              disabledDate={(current) => current && current > dayjs().endOf("day")}
              style={{ width: "100%" }}
            />
          </Col>

          <Col xs={12} md={3}>
            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} loading={loading} block>
              Search
            </Button>
          </Col>

          <Col xs={12} md={3}>
            <Button type="default" icon={<DownloadOutlined />} onClick={handleDownload} disabled={!data.length} block>
              Download
            </Button>
          </Col>
        </Row>

        <div style={{ marginTop: 20, overflowX: "auto", width: "100%" }}>
          <Table
            columns={columns}
            dataSource={data}
            rowKey={(record, index) => index}
            scroll={{ x: "max-content" }}
            size="small"
            pagination={{ pageSize: 10, responsive: true }}
            loading={loading}
            bordered
          />
        </div>
      </Card>

      <style>{`
        @media (max-width: 768px) {
          .ant-table { font-size: 12px !important; }
          .ant-table-cell { padding: 6px 8px !important; white-space: nowrap; }
          .ant-tag { font-size: 11px !important; }
          .ant-card-body { padding: 12px !important; }
        }
      `}</style>
    </div>
  );
};

export default DiseaseReport;